import React, { useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Box, Button, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, Alert } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import { DEFAULT_REVIEW_ROUTING, routingFor } from '../../utils/conditionWorkflow';
import { ROLE_DEFINITIONS } from '../../../../services/roleService';

const SUBMITTERS = [
  ['respondent', 'Individual Contributor'],
  ['team_lead', 'Frontline Supervisor / Team Leader'],
  ['department_lead', 'Department Head'],
  ['site_manager', 'Facility/Site/Plant Manager'],
  ['executive', 'Executive'],
  ['org_admin', 'Org Admin'],
];
const REVIEWERS = [
  ['team_lead', 'FLS of the NWT'],
  ['department_lead', 'Department Head'],
  ['site_manager', 'Facility/Site/Plant Manager'],
  ['org_admin', 'Org Admin (REPSit.ai SME)'],
];

/**
 * Review routing (Beta-1 plan P2-37; Tim C0820 / TICK C45): who reviews whose OE Conditions.
 * Defaults route by the entrant's role; an organization can change any row. Stored on
 * `Hugo_Organizations.reviewRouting`.
 */
export function ReviewRoutingSection({ organization, onSave, saving }) {
  const [draft, setDraft] = useState(organization?.reviewRouting || {});
  const [dirty, setDirty] = useState(false);
  const rowFor = (role) => routingFor(role, draft);
  const set = (role, patch) => {
    setDraft((d) => ({ ...d, [role]: { ...rowFor(role), ...patch } }));
    setDirty(true);
  };
  const reset = () => {
    setDraft({});
    setDirty(true);
  };
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AltRouteIcon color="primary" />
          <Typography variant="h6">Review Routing</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Who reviews an OE Condition depends on who logged it. For Department Head and below, approval is routed to whoever the
          person reports to; for Facility/Site/Plant Manager and above, the approval authority is the REPSit.ai‑certified
          SME / administrator. Change any row for your organization; a single‑step review has no L2.
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Logged by</TableCell>
              <TableCell>L1 review</TableCell>
              <TableCell>L2 approval</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SUBMITTERS.map(([role, label]) => {
              const r = rowFor(role);
              const isDefault = JSON.stringify(r) === JSON.stringify(DEFAULT_REVIEW_ROUTING[role]);
              return (
                <TableRow key={role}>
                  <TableCell>
                    {label}
                    {!isDefault && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                        customized
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField select size="small" value={r.l1} onChange={(e) => set(role, { l1: e.target.value })} sx={{ minWidth: 220 }}>
                      {REVIEWERS.map(([v, l]) => (
                        <MenuItem key={v} value={v}>{l}</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField select size="small" value={r.l2 || ''} onChange={(e) => set(role, { l2: e.target.value || null })} sx={{ minWidth: 220 }}>
                      <MenuItem value="">
                        <em>None — single step</em>
                      </MenuItem>
                      {REVIEWERS.map(([v, l]) => (
                        <MenuItem key={v} value={v}>{l}</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </Box>
        {dirty && <Alert severity="info" sx={{ mt: 2 }}>Unsaved changes.</Alert>}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
          <Button size="small" onClick={reset} disabled={saving}>
            Restore defaults
          </Button>
          <Button size="small" variant="contained" disabled={!dirty || saving} onClick={async () => { await onSave(draft); setDirty(false); }}>
            Save routing
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Roles: {Object.values(ROLE_DEFINITIONS).map((d) => d.label).join(' · ')}.
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

export default ReviewRoutingSection;
